<template>
  <div class="top-sales-page pa-6">
    <h2 class="mb-6">Топ продаж</h2>

    <v-card>
      <v-card-title class="d-flex align-center">
        <v-btn color="success" @click="openCreateDialog" :disabled="isLoading">
          <v-icon left>mdi-plus</v-icon>
          Добавить
        </v-btn>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Поиск по ключевому слову"
          single-line
          hide-details
          clearable
          class="mr-4"
          style="max-width: 320px"
        ></v-text-field>
        <v-btn icon @click="fetchAllTopSales" :loading="isLoading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        :loading="isLoading"
        loading-text="Загрузка..."
        no-data-text="Нет данных для отображения"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-options': [10, 25, 50],
          'items-per-page-text': 'Записей на странице'
        }"
      >
        <template v-slot:item.actions="{ item }">
          <v-btn color="info" x-small icon @click="openEditDialog(item)" class="mr-1">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn color="error" x-small icon @click="askDelete(item)">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="showDialog" max-width="550" persistent>
      <v-card>
        <v-card-title class="headline">{{ formTitle }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="edited.keyword"
            label="Ключевое слово"
            counter="100"
            maxlength="100"
            outlined
            dense
          ></v-text-field>

          <v-text-field
            v-model.number="edited.goodsId"
            label="ID товара"
            type="number"
            min="1"
            outlined
            dense
            :loading="isNameLoading"
            :error-messages="goodsNameError"
            :hint="goodsName ? `Товар: ${goodsName}` : ''"
            persistent-hint
            @input="onGoodsIdInput"
          ></v-text-field>

          <v-text-field
            v-model.number="edited.rank"
            label="Рейтинг"
            type="number"
            outlined
            dense
            hint="Чем выше рейтинг, тем выше товар в выдаче"
            persistent-hint
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeDialog">Отмена</v-btn>
          <v-btn color="primary" @click="save" :disabled="!canSave" :loading="isSaving">
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DeleteConfirmDialog
      v-model="showDeleteConfirm"
      title="Подтверждение удаления"
      :message="deleteMessage"
      :loading="isDeleting"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'TopSales',

  data() {
    return {
      search: '',

      showDialog: false,
      edited: this.emptyItem(),
      editedId: null,
      isSaving: false,

      goodsName: null,
      goodsNameError: '',
      isNameLoading: false,
      nameLookupTimer: null,

      showDeleteConfirm: false,
      deleteTarget: null,
      isDeleting: false,

      headers: [
        { text: 'Ключевое слово', value: 'keyword' },
        { text: 'ID товара', value: 'goodsId', width: '120px' },
        { text: 'Название товара', value: 'goodsFullName' },
        { text: 'Рейтинг', value: 'rank', width: '120px' },
        { text: 'Действия', value: 'actions', sortable: false, width: '110px', align: 'center' }
      ]
    }
  },

  computed: {
    ...mapState('topSales', ['items', 'isLoading']),

    formTitle() {
      return this.editedId === null ? 'Создание записи' : 'Изменение записи'
    },

    canSave() {
      return this.edited.keyword &&
             this.edited.keyword.trim().length > 0 &&
             this.edited.keyword.length <= 100 &&
             this.edited.goodsId > 0 &&
             this.goodsName &&
             Number.isInteger(this.edited.rank) &&
             !this.isSaving
    },

    deleteMessage() {
      if (!this.deleteTarget) return ''
      return `Удалить ключевое слово «${this.deleteTarget.keyword}» для товара «${this.deleteTarget.goodsFullName}»?`
    }
  },

  mounted() {
    this.fetchAllTopSales()
  },

  beforeDestroy() {
    clearTimeout(this.nameLookupTimer)
  },

  methods: {
    ...mapActions('topSales', [
      'fetchAllTopSales',
      'createTopSales',
      'updateTopSales',
      'deleteTopSales',
      'fetchGoodsName',
      'clearError'
    ]),

    emptyItem() {
      return { keyword: '', goodsId: null, rank: null }
    },

    openCreateDialog() {
      this.editedId = null
      this.edited = this.emptyItem()
      this.goodsName = null
      this.goodsNameError = ''
      this.showDialog = true
    },

    openEditDialog(item) {
      this.editedId = item.id
      this.edited = { keyword: item.keyword, goodsId: item.goodsId, rank: item.rank }
      this.goodsName = item.goodsFullName
      this.goodsNameError = ''
      this.showDialog = true
    },

    closeDialog() {
      this.showDialog = false
      this.clearError()
      clearTimeout(this.nameLookupTimer)
    },

    onGoodsIdInput() {
      this.goodsName = null
      this.goodsNameError = ''
      clearTimeout(this.nameLookupTimer)

      const goodsId = this.edited.goodsId
      if (!goodsId || goodsId <= 0) return

      this.nameLookupTimer = setTimeout(() => this.lookupGoodsName(goodsId), 400)
    },

    async lookupGoodsName(goodsId) {
      this.isNameLoading = true
      try {
        const name = await this.fetchGoodsName(goodsId)
        // Пока грузилось, пользователь мог поменять ID — ответ уже неактуален
        if (this.edited.goodsId !== goodsId) return

        if (name === null) {
          this.goodsName = null
          this.goodsNameError = 'Товар с таким ID не найден'
        } else {
          this.goodsName = name
          this.goodsNameError = ''
        }
      } catch (err) {
        window.console.log(err)
      } finally {
        this.isNameLoading = false
      }
    },

    async save() {
      if (!this.canSave) return

      this.isSaving = true
      try {
        if (this.editedId === null) {
          await this.createTopSales({
            keyword: this.edited.keyword.trim(),
            goodsId: this.edited.goodsId,
            rank: this.edited.rank
          })
        } else {
          await this.updateTopSales({
            id: this.editedId,
            keyword: this.edited.keyword.trim(),
            goodsId: this.edited.goodsId,
            rank: this.edited.rank
          })
        }

        this.showDialog = false
        await this.fetchAllTopSales()
      } catch (err) {
        window.console.log(err)
      } finally {
        this.isSaving = false
      }
    },

    askDelete(item) {
      this.deleteTarget = item
      this.showDeleteConfirm = true
    },

    cancelDelete() {
      this.showDeleteConfirm = false
      this.deleteTarget = null
    },

    async confirmDelete() {
      const target = this.deleteTarget
      this.isDeleting = true

      try {
        await this.deleteTopSales(target.id)
        this.showDeleteConfirm = false
        this.deleteTarget = null
        await this.fetchAllTopSales()
      } catch (err) {
        window.console.log(err)
      } finally {
        this.isDeleting = false
      }
    }
  }
}
</script>

<style scoped>
.top-sales-page {
  max-width: 1200px;
  margin: 0 auto;
}
</style>
